// src/lib/googlePlaces.js
//
// Lazy-loader for the Google Places JS SDK plus a tiny helper that converts a
// Google `PlaceResult` (or autocomplete prediction's resolved place) into the
// `service_address` shape the port-in API expects:
//
//   { street1, street2, city, state, zip, google_place_id }
//
// Loading model:
//   - Script is injected once, on first call. Promise is cached so subsequent
//     calls reuse the same load.
//   - If `REACT_APP_GOOGLE_PLACES_API_KEY` is not set, `isConfigured()` returns
//     false and the wizard falls back to a manual address form.
//
// Why we use the "places" library and not the new PlaceAutocompleteElement:
//   The classic `google.maps.places.Autocomplete` widget integrates cleanly with
//   a regular <input> element, which is what the wizard uses. The newer web
//   component is fine for greenfield but adds DOM-attach concerns inside our
//   modal context. Revisit if Google deprecates the classic widget.

const SCRIPT_ID = 'google-places-sdk';

let loadPromise = null;

export function isConfigured() {
  return !!process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
}

export function loadGooglePlaces() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Places can only load in the browser.'));
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }
  if (loadPromise) return loadPromise;

  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_PLACES_API_KEY is not set.'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Google Places script failed to load.')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Google Places script failed to load.'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Convert a Google `PlaceResult.address_components` array into the service
 * address shape the port-in API expects. Returns null when the place doesn't
 * carry the minimum fields (street_number + route + locality + state + zip).
 */
export function placeResultToServiceAddress(place) {
  if (!place || !Array.isArray(place.address_components)) return null;
  const get = (type) =>
    place.address_components.find((c) => Array.isArray(c.types) && c.types.includes(type));

  const streetNumber = get('street_number')?.long_name ?? '';
  const route = get('route')?.long_name ?? '';
  const subpremise = get('subpremise')?.long_name ?? '';
  const city =
    get('locality')?.long_name ||
    get('sublocality')?.long_name ||
    get('postal_town')?.long_name ||
    '';
  const state = get('administrative_area_level_1')?.short_name ?? '';
  const zip = get('postal_code')?.long_name ?? '';

  const street1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  const street2 = subpremise ? `Suite ${subpremise}` : '';

  if (!street1 || !city || !state || !zip) return null;

  return {
    street1,
    street2,
    city,
    state,
    zip,
    google_place_id: place.place_id ?? null,
  };
}
